import type { ColumnDef } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DEVICE_MODEL_LABELS } from '@/constants/device';
import { cn } from '@/lib/utils';
import type { IConfigurationLog } from '@/types/production/configuration-log';

export type ConfigurationLogRow = IConfigurationLog;

interface ConfigurationLogTableProps {
  data: ConfigurationLogRow[];
}

const statuses = {
  true: 'text-green-500 bg-green-800/20',
  false: 'text-rose-500 bg-rose-800/20',
};

const textColor = {
  true: 'text-green-800',
  false: 'text-rose-800',
};

const info = {
  true: 'Sucesso',
  false: 'Falha',
};

export function ConfigurationLogTable({ data }: ConfigurationLogTableProps) {
  const configurationLogColumns: ColumnDef<ConfigurationLogRow>[] = [
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => {
        const log = row.original;
        const s = String(log.status) as keyof typeof statuses;
        return (
          <div className="flex items-center gap-1">
            <div className={cn(statuses[s], 'flex-none rounded-full p-1 w-fit')}>
              <div className="h-1.5 w-1.5 rounded-full bg-current" />
            </div>
            <div className={cn('hidden font-semibold sm:block', textColor[s])}>{info[s]}</div>
          </div>
        );
      },
    },
    {
      header: 'Serial',
      accessorKey: 'equipment.serial',
      cell: ({ row }) => {
        const log = row.original;
        return <p title={log.equipment.serial}>{log.equipment.serial || '--'}</p>;
      },
    },
    {
      header: 'Modelo',
      accessorKey: 'technology.system_name',
      cell: ({ row }) => {
        const log = row.original;
        return (
          <p title={log.technology.system_name}>
            {DEVICE_MODEL_LABELS[log.technology.system_name as keyof typeof DEVICE_MODEL_LABELS] ||
              '--'}
          </p>
        );
      },
    },
    {
      header: 'Nome do perfil',
      accessorKey: 'desired_profile.name',
      cell: ({ row }) => {
        const log = row.original;
        return <p>{log.desired_profile.name}</p>;
      },
    },
    {
      header: 'Usuário',
      accessorKey: 'user.name',
      cell: ({ row }) => {
        const log = row.original;
        return <p>{log.user.name}</p>;
      },
    },
    {
      header: 'Data',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const log = row.original;
        const date = new Date(log.created_at);
        return <p>{date.toLocaleString('pt-BR')}</p>;
      },
    },
    {
      header: 'Ação',
      accessorKey: 'actions',
      cell: ({ row }) => {
        const log = row.original;
        return (
          <Link to={`/production/logs/configurator/${log.id}`}>
            <Button variant="ghost" size="sm">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </Link>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns: configurationLogColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="w-full border-t">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={configurationLogColumns.length} className="h-24 text-center">
                Nenhum dado encontrado
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
